// Rug Room Shots: "Rooms" tab. Style x shape matrix of active rooms (red cell =
// no active room, with the gap targets waiting), and the room grid with
// Activate / Retire / Change base. Loads on open and on Refresh (SPEC 5.2).
// Add room (upload a photo, AddRoomDialog) and Add style / "Define XX" (AddStyleDialog)
// follow SPEC 10; both stay disabled until /config features say the worker supports them.
// 2026-10-21 build spec 9.6: every room shows its room type (the 7th letter: L · Living room (accent chair), ...);
// a room whose base photo shows an oval rug is marked (never picked automatically); SU (Sunroom) is a fixed style,
// so its rooms activate like any other; the "Outdoor & sunroom list" and "Room type rules" panels live here.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Button, Chip, Alert, CircularProgress, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MdRefresh, MdFiberNew, MdPushPin, MdSwapHoriz, MdTravelExplore, MdAddPhotoAlternate, MdLabel,
} from 'react-icons/md';
import rrsApi from './api';
import AddRoomDialog from './AddRoomDialog';
import AddStyleDialog from './AddStyleDialog';
import SceneListPanel from './SceneListPanel';
import RoomRulesPanel from './RoomRulesPanel';
import {
  AI_COLOR, primaryButtonSx, fmtInt, errText, styleName, shapeName, Tile, StyleChip, NyTime, ImageLightbox, ConfirmDialog,
} from './common';
import { roomLetterOf, letterName, baseShapeLabel, baseShapeTip } from './roomRules';

const ROOM_STATUS = {
  active: { label: 'Active', color: 'success' },
  proposed: { label: 'Proposed', color: 'info' },
  retired: { label: 'Retired', color: 'default' },
};

const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n > 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} KB`;
};

const NOT_READY_TIP = 'The worker has not been updated for this yet.';

const sourceLabel = (r) => {
  if (r.source === 'seed') return 'Seed list';
  if (r.source === 'manual') return `Added on this page${r.pinnedBy ? ` by ${r.pinnedBy}` : ''}`;
  return 'Found in safaviehimages';
};

const RoomsTab = ({ config, notify, health, onHealthRefresh, onConfigRefresh, onRoomRulesChanged }) => {
  const theme = useTheme();
  const styles = config.styles;
  const styleCodes = useMemo(() => (styles || []).map((s) => s.code), [styles]);
  const features = config.features || {};
  const [matrix, setMatrix] = useState(null);
  const [rooms, setRooms] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [styleF, setStyleF] = useState('');
  const [typeF, setTypeF] = useState('');
  const [statusF, setStatusF] = useState('');
  const [confirm, setConfirm] = useState(null); // { room, status, text }
  const [confirmNote, setConfirmNote] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [baseRoom, setBaseRoom] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [candError, setCandError] = useState('');
  const [baseChoice, setBaseChoice] = useState('');
  const [baseBusy, setBaseBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [addRoomOpen, setAddRoomOpen] = useState(false);
  const [addStyleOpen, setAddStyleOpen] = useState(false);
  const [addStyleCode, setAddStyleCode] = useState('');

  const loadMatrix = useCallback(async () => {
    try { setMatrix(await rrsApi.roomMatrix()); } catch (e) { setError(errText(e)); }
  }, []);

  const loadRooms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rrsApi.listRooms({ style: styleF, type: typeF, status: statusF });
      setRooms(res.items || []);
      setError('');
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [styleF, typeF, statusF]);

  useEffect(() => { loadMatrix(); }, [loadMatrix]);
  useEffect(() => { loadRooms(); }, [loadRooms]);

  const refresh = () => { loadMatrix(); loadRooms(); };

  const requestScan = async () => {
    setScanBusy(true);
    try {
      await rrsApi.requestRoomScan();
      notify('Room scan requested. The worker looks for new coded ROOMAI files on its next pass; new rooms appear here as Proposed.', 'info');
      if (onHealthRefresh) onHealthRefresh();
    } catch (e) {
      notify(errText(e), 'error');
    } finally {
      setScanBusy(false);
    }
  };

  const cellOf = useCallback((s, t) => (matrix && (matrix.cells || []).find((c) => c.style === s && c.shape === t)) || null, [matrix]);

  // Proposed first ("New"), then active, then retired; code order inside.
  const sortedRooms = useMemo(() => {
    const order = { proposed: 0, active: 1, retired: 2 };
    return [...(rooms || [])].sort((a, b) => ((order[a.status] ?? 3) - (order[b.status] ?? 3)) || String(a.roomCode).localeCompare(String(b.roomCode)));
  }, [rooms]);

  const askStatus = (room, status) => {
    let text;
    if (status === 'active') {
      const cell = cellOf(room.styleCode, room.typeCode);
      const waiting = cell ? cell.gapTargets || 0 : 0;
      text = waiting
        ? `This re-plans ${fmtInt(waiting)} waiting target(s) (${styleName(room.styleCode, styles)} x ${shapeName(room.typeCode, config.shapes)}) and pins the current base.`
        : 'This makes the room available to new plans and pins the current base.';
    } else {
      const planned = (room.usage && room.usage.plannedTargets) || 0;
      text = planned
        ? `This re-plans ${fmtInt(planned)} planned target(s) that use this room. Generated images keep their room.`
        : 'No planned target uses this room. Generated images keep their room.';
    }
    setConfirmNote('');
    setConfirm({ room, status, text });
  };

  const doStatus = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      const res = await rrsApi.setRoomStatus(confirm.room.roomCode, { status: confirm.status, note: confirmNote.trim() });
      notify(`${confirm.room.roomCode} is now ${confirm.status}${res.replannedTargets ? `; ${fmtInt(res.replannedTargets)} target(s) re-planning` : ''}.`, 'success');
      setConfirm(null);
      refresh();
    } catch (e) {
      const code = e.body && e.body.code;
      if (code === 'unknown_style') notify(`This room code does not start with a known style (${styleCodes.join(', ')}). Add the style first with Add style.`, 'error');
      else if (code === 'no_base') notify('This room has no base image yet. Pick one with Change base first.', 'error');
      else notify(errText(e), 'error');
    } finally {
      setConfirmBusy(false);
    }
  };

  const openBase = async (room) => {
    setBaseRoom(room);
    setCandidates(null);
    setCandError('');
    setBaseChoice((room.base && room.base.imageKey) || '');
    try {
      const res = await rrsApi.roomCandidates(room.roomCode);
      setCandidates(res.items || []);
    } catch (e) {
      setCandError(errText(e));
      setCandidates([]);
    }
  };

  const doBase = async () => {
    if (!baseRoom || !baseChoice) return;
    setBaseBusy(true);
    try {
      const res = await rrsApi.setRoomBase(baseRoom.roomCode, baseChoice);
      const moved = res && res.unstartedTargetsUpdated ? ` ${fmtInt(res.unstartedTargetsUpdated)} planned target(s) now use it.` : '';
      notify(`${baseRoom.roomCode}: base set to ${baseChoice} and pinned.${moved} Already-queued images keep their base.`, 'success');
      setBaseRoom(null);
      loadRooms();
    } catch (e) {
      notify(errText(e), 'error');
    } finally {
      setBaseBusy(false);
    }
  };

  const mStyles = (matrix && matrix.styles) || styleCodes;
  const unknownCodes = (matrix && Array.isArray(matrix.unknownStyleCodes)) ? matrix.unknownStyleCodes.filter(Boolean) : [];

  const openAddStyle = (code) => { setAddStyleCode(code || ''); setAddStyleOpen(true); };
  // After Add style: /config gets the new style list (and its colours); the matrix gains its row.
  const onStyleAdded = () => { if (onConfigRefresh) onConfigRefresh(); refresh(); };
  const mShapes = (matrix && matrix.shapes) || config.shapes.map((s) => s.code);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Active rooms by style and shape</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {health && health.worker && health.worker.lastRoomDiscovery && (
            <Typography variant="caption" color="text.secondary">
              Last scan <NyTime ts={health.worker.lastRoomDiscovery} variant="caption" />
              {health.roomScanRequestedAt ? ' · new scan requested' : ''}
            </Typography>
          )}
          <Tooltip title="Ask the worker to look for new coded ROOMAI files in safaviehimages">
            <span><Button startIcon={<MdTravelExplore />} onClick={requestScan} disabled={scanBusy}>Scan for new rooms</Button></span>
          </Tooltip>
          <Tooltip title={features.addRoom ? 'Upload a room photo for a style and shape' : NOT_READY_TIP}>
            <span>
              <Button startIcon={<MdAddPhotoAlternate />} onClick={() => setAddRoomOpen(true)} disabled={!features.addRoom}>Add room</Button>
            </span>
          </Tooltip>
          <Tooltip title={features.addStyle ? 'Define a new style code (chosen by hand only)' : NOT_READY_TIP}>
            <span>
              <Button startIcon={<MdLabel />} onClick={() => openAddStyle('')} disabled={!features.addStyle}>Add style</Button>
            </span>
          </Tooltip>
          <Button startIcon={<MdRefresh />} onClick={refresh}>Refresh</Button>
        </Stack>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {unknownCodes.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Some rooms use style codes that are not defined yet: {unknownCodes.join(', ')}.
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
            {unknownCodes.map((c) => (
              <Tooltip key={c} title={features.addStyle ? '' : NOT_READY_TIP}>
                <span>
                  <Button size="small" variant="outlined" color="inherit" disabled={!features.addStyle} onClick={() => openAddStyle(c)}>
                    Define {c}
                  </Button>
                </span>
              </Tooltip>
            ))}
          </Stack>
        </Alert>
      )}

      {/* Matrix */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Style</TableCell>
              {mShapes.map((t) => (
                <TableCell key={t} align="center">
                  <Tooltip title={shapeName(t, config.shapes)}><span>{t}</span></Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!matrix && (
              <TableRow><TableCell colSpan={mShapes.length + 1} sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={22} /></TableCell></TableRow>
            )}
            {matrix && mStyles.map((s) => (
              <TableRow key={s}>
                <TableCell><StyleChip code={s} styles={styles} /></TableCell>
                {mShapes.map((t) => {
                  const c = cellOf(s, t) || { activeRooms: 0, proposedRooms: 0, gapTargets: 0 };
                  const empty = !c.activeRooms;
                  const selected = styleF === s && typeF === t;
                  return (
                    <TableCell
                      key={t} align="center"
                      onClick={() => { setStyleF(selected ? '' : s); setTypeF(selected ? '' : t); }}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: empty ? (theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.18)' : 'rgba(244,67,54,0.09)') : undefined,
                        outline: selected ? `2px solid ${AI_COLOR}` : 'none', outlineOffset: -2,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: empty ? 'error.main' : 'text.primary' }}>{c.activeRooms || 0}</Typography>
                      {c.gapTargets ? <Typography variant="caption" color="error.main" display="block">{fmtInt(c.gapTargets)} waiting</Typography> : null}
                      {c.proposedRooms ? <Typography variant="caption" color="info.main" display="block">+{c.proposedRooms} proposed</Typography> : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Outdoor & sunroom list, room-type rules (collapsed until opened) */}
      <SceneListPanel notify={notify} />
      <RoomRulesPanel notify={notify} letters={config.roomLetters} onChanged={onRoomRulesChanged} />

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
        <TextField select size="small" label="Style" value={styleF} sx={{ minWidth: 170 }} onChange={(e) => setStyleF(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {styles.map((s) => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Type (shape)" value={typeF} sx={{ minWidth: 190 }} onChange={(e) => setTypeF(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {config.shapes.map((s) => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={statusF} sx={{ minWidth: 150 }} onChange={(e) => setStatusF(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(ROOM_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        {loading && <CircularProgress size={20} />}
        <Typography variant="body2" color="text.secondary">{rooms ? `${fmtInt(rooms.length)} room(s)` : ''}</Typography>
      </Stack>

      {/* Room grid */}
      {rooms && rooms.length === 0 && !loading && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">No rooms match these filters.</Typography>
        </Paper>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 1.5 }}>
        {sortedRooms.map((r) => {
          const base = r.base || {};
          const unknownStyle = r.knownStyle !== undefined ? !r.knownStyle : !styleCodes.includes(r.styleCode);
          const letter = r.letter || roomLetterOf(r.roomCode);
          return (
            <Paper key={r.roomCode} variant="outlined" sx={{ p: 1.25, borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
              <Tile
                src={base.smallUrl} alt={r.roomCode} size={200} sx={{ width: '100%', height: 'auto', aspectRatio: '1 / 1' }}
                onClick={base.smallUrl ? () => setLightbox({
                  title: `${r.roomCode} · ${base.imageKey || ''}`, initialKey: 'base',
                  // largeUrl (1600 px tier; a presigned link for page-added rooms) when the backend sends it.
                  items: [{ key: 'base', label: 'Base', src: base.largeUrl || base.originalUrl || base.smallUrl, downloadUrl: base.originalUrl, downloadLabel: 'Open original' }],
                }) : undefined}
              >
                {r.status === 'proposed' && (
                  <Chip size="small" icon={<MdFiberNew />} label="New" color="info" sx={{ position: 'absolute', top: 6, left: 6 }} />
                )}
              </Tile>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{r.roomCode}</Typography>
                <Chip size="small" label={(ROOM_STATUS[r.status] || {}).label || r.status} color={(ROOM_STATUS[r.status] || {}).color || 'default'} />
              </Stack>
              {letter && (
                <Typography variant="caption" color="text.secondary">
                  {letter} · {r.letterName || letterName(letter, config.roomLetters)}
                </Typography>
              )}
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                {unknownStyle
                  ? <Chip size="small" color="error" variant="outlined" label={`unknown style ${r.styleCode}`} />
                  : <StyleChip code={r.styleCode} styles={styles} />}
                <Tooltip title={shapeName(r.typeCode, config.shapes)}><Chip size="small" variant="outlined" label={r.typeCode} /></Tooltip>
                {base.shapeOk === false && (
                  <Tooltip title={`${baseShapeTip(base)} This room is for ${shapeName(r.typeCode, config.shapes)} rugs.`}>
                    <Chip size="small" color="warning" label={baseShapeLabel(base)} />
                  </Tooltip>
                )}
                {base.square === false && (
                  <Tooltip title={`Base is ${base.w || '?'} x ${base.h || '?'}; the output is 1:1, check the composition.`}>
                    <Chip size="small" color="warning" variant="outlined" label="not square" />
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                {sourceLabel(r)} · {fmtInt(r.candidates || 0)} candidate(s)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Used: {fmtInt((r.usage && r.usage.generations) || 0)} generation(s), {fmtInt((r.usage && r.usage.plannedTargets) || 0)} planned
              </Typography>
              {r.pinnedBy && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <MdPushPin size={12} /> {r.pinnedBy}
                </Typography>
              )}
              {r.firstSeenAt && <Typography variant="caption" color="text.secondary">First seen <NyTime ts={r.firstSeenAt} variant="caption" /></Typography>}
              {r.note && <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>{r.note}</Typography>}
              <Box sx={{ flex: 1 }} />
              <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
                {r.status !== 'active' && (
                  <Tooltip title={unknownStyle ? 'Unknown style code: cannot be activated' : (!base.imageKey ? 'Pick a base first' : '')}>
                    <span>
                      <Button size="small" variant="contained" disabled={unknownStyle || !base.imageKey} onClick={() => askStatus(r, 'active')} sx={primaryButtonSx}>
                        Activate
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {r.status === 'active' && (
                  <Button size="small" variant="outlined" color="error" onClick={() => askStatus(r, 'retired')}>Retire</Button>
                )}
                <Button size="small" startIcon={<MdSwapHoriz />} onClick={() => openBase(r)}>Change base</Button>
              </Stack>
            </Paper>
          );
        })}
      </Box>

      {/* Activate / retire confirm */}
      <ConfirmDialog
        open={!!confirm} busy={confirmBusy} onClose={() => setConfirm(null)} onConfirm={doStatus}
        title={confirm ? `${confirm.status === 'active' ? 'Activate' : 'Retire'} ${confirm.room.roomCode}?` : ''}
        confirmLabel={confirm && confirm.status === 'active' ? 'Activate' : 'Retire'}
        confirmColor={confirm && confirm.status === 'retired' ? 'error' : undefined}
      >
        {confirm && (
          <>
            <Typography sx={{ mb: 1.5 }}>{confirm.text}</Typography>
            <TextField size="small" fullWidth label="Note (optional)" value={confirmNote} onChange={(e) => setConfirmNote(e.target.value.slice(0, 500))} />
          </>
        )}
      </ConfirmDialog>

      {/* Change base */}
      <Dialog open={!!baseRoom} onClose={baseBusy ? undefined : () => setBaseRoom(null)} maxWidth="md" fullWidth>
        {baseRoom && (
          <>
            <DialogTitle>
              Base image for {baseRoom.roomCode}
              <Typography variant="body2" color="text.secondary">
                Every coded ROOMAI file for this room. Best first: right rug shape, newest, largest. The choice is pinned.
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {candError && <Alert severity="error">{candError}</Alert>}
              {!candidates && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
              {candidates && candidates.length === 0 && !candError && <Typography color="text.secondary">No candidate files found.</Typography>}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.25 }}>
                {(candidates || []).map((c) => {
                  const sel = baseChoice === c.imageKey;
                  return (
                    <Paper
                      key={c.imageKey} variant="outlined" onClick={() => setBaseChoice(c.imageKey)}
                      sx={{ p: 1, cursor: 'pointer', borderWidth: 2, borderColor: sel ? AI_COLOR : 'divider', bgcolor: sel ? `${AI_COLOR}14` : 'transparent' }}
                    >
                      <Tile src={c.smallUrl} alt={c.imageKey} size={150} sx={{ width: '100%', height: 150 }} />
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5, wordBreak: 'break-all' }}>{c.sku}</Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                        {c.isCurrentBase && <Chip size="small" label="current" sx={{ height: 18, fontSize: '0.65rem' }} />}
                        {c.shapeOk === false && <Chip size="small" color="warning" label={`rug ${c.skuShape || '?'}`} sx={{ height: 18, fontSize: '0.65rem' }} />}
                        {c.skuIsRug === false && <Chip size="small" color="warning" variant="outlined" label="not a rug SKU" sx={{ height: 18, fontSize: '0.65rem' }} />}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {fmtBytes(c.fileBytes)}{c.lastModified ? ` · ${String(c.lastModified).slice(0, 10)}` : ''}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setBaseRoom(null)} disabled={baseBusy}>Cancel</Button>
              <Button
                variant="contained" onClick={doBase} sx={primaryButtonSx}
                disabled={baseBusy || !baseChoice || (baseRoom.base && baseRoom.base.imageKey === baseChoice)}
              >
                {baseBusy ? <CircularProgress size={20} color="inherit" /> : 'Use this base'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <AddRoomDialog
        open={addRoomOpen} onClose={() => setAddRoomOpen(false)} config={config} matrix={matrix} notify={notify}
        onAdded={refresh}
      />
      <AddStyleDialog
        open={addStyleOpen} onClose={() => setAddStyleOpen(false)} initialCode={addStyleCode} config={config}
        notify={notify} onAdded={onStyleAdded}
      />

      <ImageLightbox
        open={!!lightbox} onClose={() => setLightbox(null)}
        items={lightbox ? lightbox.items : []} initialKey={lightbox ? lightbox.initialKey : null}
        title={lightbox ? lightbox.title : ''}
      />
    </Box>
  );
};

export default RoomsTab;
